/**
 * Stop a modal closing when a drag that STARTED inside it ends on its backdrop.
 *
 * Glen, 2026-10-28: selecting text in a field by click-and-drag and letting go
 * outside the dialog closed the dialog (and lost what was typed). The cause is
 * the browser, not any one modal: a `click` fires on the nearest common
 * ancestor of the mousedown and mouseup targets — and for a drag from the
 * panel out to the backdrop that ancestor IS the backdrop, so every
 * `onClick={e => e.target === e.currentTarget && onClose()}` sees a genuine
 * backdrop click. There are dozens of hand-rolled overlays across the app, so
 * this is fixed once, globally, rather than in each one.
 *
 * Rule: a click whose target is a full-screen overlay element (`inset-0` and
 * `fixed`/`absolute` — the shapes every backdrop in the app uses, see the
 * Z-INDEX SCALE in index.css) is swallowed when the press began on a
 * different element. A press that starts AND ends on the backdrop still
 * closes, as before. Clicks on anything else are untouched, so an ordinary
 * click that starts on a button's icon and ends on its label still works.
 *
 * Capture phase on document, so it runs before React's own listeners (on the
 * root and on portal containers) and the modal's onClick never sees the event.
 * Radix dialogs (components/ui/dialog) already ignore this case; this does not
 * interfere with them.
 */
let pressTarget = null;

function isOverlay(el) {
  if (!el || !el.classList) return false;
  const c = el.classList;
  return c.contains('inset-0') && (c.contains('fixed') || c.contains('absolute'));
}

export function installOverlayDragGuard() {
  if (typeof document === 'undefined' || installOverlayDragGuard.installed) return;
  installOverlayDragGuard.installed = true;
  document.addEventListener('pointerdown', (e) => { pressTarget = e.target; }, true);
  document.addEventListener('click', (e) => {
    const started = pressTarget;
    pressTarget = null;
    if (started && started !== e.target && isOverlay(e.target)) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, true);
}
